import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { useQuery } from '@tanstack/react-query';
import Loader from 'components/Kizen/Loader';
import { PageSizing } from 'components/Layout/PageContentWidth';
import { getBusinessLicenseQuery, getSummaryUsageQuery } from 'query/billing';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { StatisticRow } from './components/StatisticRow';
import { format } from 'date-fns';
import { Button } from '@kizen/kds/Button';
import { ContentCard, PageWrapper } from '../styles';
import { ObjectCard } from './components/ObjectCard';
import useModal from '__components/Modals/useModal';
import Modal from 'components/Modals/presets/BasicModal';
import { Link } from 'react-router-dom';
import { useBillingPermissions } from 'ts-components/hooks/permissions/billing';

export const UsagePage = () => {
  const { t } = useTranslation();

  const { data: summaryData, isLoading: summaryUsageLoading } = useQuery(
    getSummaryUsageQuery()
  );

  const { data: businessLicenses, isLoading: businessLicensesLoading } =
    useQuery(getBusinessLicenseQuery());

  const { canManagePlans } = useBillingPermissions();

  const generalPlan = useMemo(() => {
    return businessLicenses?.find(
      (l) => l.license_plan.plan_category === 'general'
    );
  }, [businessLicenses]);

  const automationMonthlyStats = useMemo(() => {
    if (!summaryData?.automations?.monthly_limit) {
      return {
        percentage: 0,
        used: summaryData?.automations?.monthly_usage ?? 0,
        limit: summaryData?.automations?.monthly_limit ?? Infinity,
        isOver: false,
      };
    }

    const percentage =
      (summaryData.automations.monthly_usage /
        summaryData.automations.monthly_limit) *
      100;

    return {
      percentage,
      used: summaryData.automations.monthly_usage,
      limit: summaryData.automations.monthly_limit ?? Infinity,
      isOver: percentage >= 100,
    };
  }, [summaryData]);

  const automationDailyStats = useMemo(() => {
    if (!summaryData?.automations?.daily_limit) {
      return {
        percentage: 0,
        used: summaryData?.automations?.daily_usage ?? 0,
        limit: summaryData?.automations?.daily_limit ?? Infinity,
        isOver: false,
      };
    }

    const percentage =
      (summaryData.automations.daily_usage /
        summaryData.automations.daily_limit) *
      100;

    return {
      percentage,
      used: summaryData.automations.daily_usage,
      limit: summaryData.automations.daily_limit ?? Infinity,
      isOver: percentage >= 100,
    };
  }, [summaryData]);

  const platformUserMonthlyStats = useMemo(() => {
    if (!summaryData?.core_platform?.max_users) {
      return {
        percentage: 0,
        used: summaryData?.core_platform?.users_usage ?? 0,
        limit: summaryData?.core_platform?.max_users ?? Infinity,
        isOver: false,
      };
    }

    const percentage =
      (summaryData.core_platform.users_usage /
        summaryData.core_platform.max_users) *
      100;

    return {
      percentage,
      used: summaryData.core_platform.users_usage,
      limit: summaryData.core_platform.max_users ?? Infinity,
      isOver: percentage >= 100,
    };
  }, [summaryData]);

  const emailMonthlyStats = useMemo(() => {
    if (!summaryData?.emails?.monthly_limit) {
      return {
        percentage: 0,
        used: summaryData?.emails?.monthly_usage ?? 0,
        limit: summaryData?.emails?.monthly_limit ?? Infinity,
        isOver: false,
      };
    }

    const percentage =
      (summaryData.emails.monthly_usage / summaryData.emails.monthly_limit) *
      100;

    return {
      percentage,
      used: summaryData.emails.monthly_usage,
      limit: summaryData.emails.monthly_limit ?? Infinity,
      isOver: percentage >= 100,
    };
  }, [summaryData]);

  const customObjectStats = useMemo(() => {
    const objectKeys = Object.keys(summaryData?.custom_objects ?? {});
    const objects = objectKeys.map((key) => {
      const matchingObject = (summaryData?.custom_objects as any)[key];

      const { stats, object } = matchingObject;

      const fieldPercentage = stats.fields_limit
        ? (stats.fields_usage / stats.fields_limit) * 100
        : 0;

      const fieldsStats = {
        percentage: fieldPercentage,
        used: stats.fields_usage ?? 0,
        limit: stats.fields_limit ?? Infinity,
        isOver: fieldPercentage >= 100,
      };

      const recordPercentage = stats.records_limit
        ? (stats.records_usage / stats.records_limit) * 100
        : 0;

      const recordStats = {
        percentage: recordPercentage,
        used: stats.records_usage ?? 0,
        limit: stats.records_limit ?? Infinity,
        isOver: recordPercentage >= 100,
      };

      return {
        key,
        object,
        stats,
        fieldsStats,
        recordStats,
      };
    });

    return objects.sort((a, b) => {
      const aMaxPercentage = Math.max(
        a.fieldsStats.percentage,
        a.recordStats.percentage
      );
      const bMaxPercentage = Math.max(
        b.fieldsStats.percentage,
        b.recordStats.percentage
      );

      return bMaxPercentage - aMaxPercentage;
    });
  }, [summaryData]);

  const customObjectSummaryStats = useMemo(() => {
    const numObjects = Object.keys(summaryData?.custom_objects || {}).length;

    const numObjectsAtLimit = customObjectStats.filter(
      (s) => s.fieldsStats.isOver || s.recordStats.isOver
    ).length;

    const numObjectsNearLimit = customObjectStats.filter((s) => {
      const isAtRisk =
        s.fieldsStats.percentage >= 80 || s.recordStats.percentage >= 80;
      const isOver =
        s.fieldsStats.percentage >= 100 || s.recordStats.percentage >= 100;

      return isAtRisk && !isOver;
    }).length;

    const numObjectsOk = customObjectStats.filter(
      (s) => s.fieldsStats.percentage < 80 && s.recordStats.percentage < 80
    ).length;

    const percentObjectsAtLimit = (numObjectsAtLimit / numObjects) * 100;

    const percentObjectsNearLimit = (numObjectsNearLimit / numObjects) * 100;

    const configuredObjectLimit = (generalPlan?.config?.limits as any)
      ?.custom_object_limit;

    const objectLimit = configuredObjectLimit ?? Infinity;

    const usedPercent = configuredObjectLimit
      ? (numObjects / configuredObjectLimit) * 100
      : 100;

    return {
      limit: objectLimit,
      used: numObjects,
      percentage: usedPercent,
      isOver: false,
      divisions: {
        risk: percentObjectsNearLimit,
        over: percentObjectsAtLimit,
      },
      numObjectsAtLimit,
      numObjectsNearLimit,
      numObjectsOk,
    };
  }, [summaryData, customObjectStats, generalPlan]);

  const objectsAtLimits = useMemo(() => {
    return customObjectStats.filter(
      (s) => s.fieldsStats.isOver || s.recordStats.isOver
    );
  }, [customObjectStats]);

  const [allObjectsModalProps, , { show: showAllObjectsModal }] = useModal();

  if (summaryUsageLoading || businessLicensesLoading) {
    return <Loader loading />;
  }

  return (
    <PageSizing>
      <div className="pb-20">
        <PageWrapper>
          <div className="grid grid-cols-[2.5fr_1fr] gap-20">
            <div>
              <ContentCard>
                <Typography variant="header" size="lg" weight="semibold">
                  {t('Plan Usage')}
                </Typography>
                <Spacer size={20} />
                {generalPlan ? (
                  <Typography>
                    {t(
                      'Monthly metrics reset at the end of your billing cycle, on'
                    )}{' '}
                    {format(
                      new Date(generalPlan.renewal_date),
                      'MMMM dd, yyyy'
                    )}
                  </Typography>
                ) : null}
                <Spacer size={30} />
                <Spacer size={10} />
                <div>
                  <Typography size="lg">{t('Automations')}</Typography>
                  <Spacer size={20} />
                  <StatisticRow
                    {...automationMonthlyStats}
                    label={t('Monthly')}
                  />
                  <Spacer size={15} />
                  <StatisticRow
                    {...automationDailyStats}
                    label={t('Daily')}
                    info={t('Usage is tracked in the UTC timezone')}
                  />
                  <Spacer size={30} />
                  <Spacer size={10} />{' '}
                  <Typography size="lg">{t('Marketing Platform')}</Typography>
                  <Spacer size={20} />
                  <StatisticRow
                    {...emailMonthlyStats}
                    label={t('Emails - Monthly')}
                  />
                  <Spacer size={30} />
                  <hr className="my-0 mx-24" />
                  <Spacer size={30} />
                  <Typography size="lg">{t('Custom Objects')}</Typography>
                  <Spacer size={20} />
                  <StatisticRow
                    {...customObjectSummaryStats}
                    label={t('Total Custom Objects')}
                  />
                  <Spacer size={20} />
                  <table>
                    <tr>
                      <td className="pr-6">
                        <Typography>{t('In good standing')}:</Typography>
                      </td>
                      <td>
                        <Typography color="button-primary-hover" weight="bold">
                          {customObjectSummaryStats.numObjectsOk}{' '}
                          {customObjectSummaryStats.numObjectsOk === 1
                            ? t('Object')
                            : t('Objects')}
                        </Typography>
                      </td>
                    </tr>
                    <Spacer size={10} />
                    <tr>
                      <td className="pr-6">
                        <Typography>{t('At Risk (80%+ Capacity)')}:</Typography>
                      </td>
                      <td>
                        <Typography color="orange-200" weight="bold">
                          {customObjectSummaryStats.numObjectsNearLimit}{' '}
                          {customObjectSummaryStats.numObjectsNearLimit === 1
                            ? t('Object')
                            : t('Objects')}
                        </Typography>
                      </td>
                    </tr>
                    <Spacer size={10} />
                    <tr>
                      <td className="pr-6">
                        <Typography>{t('At Limit(s)')}:</Typography>
                      </td>
                      <td>
                        <Typography
                          color="button-warning-default"
                          weight="bold"
                        >
                          {customObjectSummaryStats.numObjectsAtLimit}{' '}
                          {customObjectSummaryStats.numObjectsAtLimit === 1
                            ? t('Object')
                            : t('Objects')}
                        </Typography>
                      </td>
                    </tr>
                  </table>
                  <Spacer size={30} />
                  {objectsAtLimits.length > 0 ? (
                    <>
                      <Typography weight="semibold">
                        {t('Objects at Limit(s)')}
                      </Typography>
                      <Spacer size={20} />
                      <div className="grid grid-cols-3 gap-20">
                        {objectsAtLimits.map((entry) => {
                          return (
                            <ObjectCard
                              key={entry.key}
                              object={entry.object}
                              fieldStats={entry.fieldsStats}
                              recordStats={entry.recordStats}
                            />
                          );
                        })}
                      </div>
                      <Spacer size={30} />
                    </>
                  ) : null}
                  <div className="flex justify-center">
                    <Button
                      outline
                      color="secondary"
                      onClick={showAllObjectsModal}
                    >
                      {t('View All Object Usage')}
                    </Button>
                  </div>
                </div>
              </ContentCard>
            </div>
            <div>
              <ContentCard>
                <Typography variant="header" size="lg" weight="semibold">
                  {t('User Summary')}
                </Typography>
                <Spacer size={20} />
                <StatisticRow
                  {...platformUserMonthlyStats}
                  label={t('Platform Users')}
                  color="bg-blue-700"
                />
                {canManagePlans ? (
                  <>
                    <Spacer size={30} />
                    <div className="flex justify-center">
                      <Link to="/settings/billing/plan">
                        <Button color="secondary">{t('Manage Users')}</Button>
                      </Link>
                    </div>
                  </>
                ) : null}
              </ContentCard>
            </div>
          </div>
        </PageWrapper>
      </div>
      <Modal
        {...allObjectsModalProps}
        heading={t('All Object Usage')}
        size="large"
        buttonText={t('Close')}
        actionBtnColor="blue"
        leftBtn={null}
      >
        <div className="grid grid-cols-3 gap-20">
          {customObjectStats.map((entry) => {
            return (
              <ObjectCard
                key={entry.key}
                object={entry.object}
                fieldStats={entry.fieldsStats}
                recordStats={entry.recordStats}
              />
            );
          })}
        </div>
      </Modal>
    </PageSizing>
  );
};
